/**
 * Live Menu Preview — Top 15 popular products from Queens, filterable by
 * category chips. Owns its own state (activeCat) and refetch effect with
 * stale-result protection.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ProductCard from "../../components/ProductCard";
import { getMenu } from "../../lib/api";

export default function HomeLiveMenuPreview({ categories }) {
    const [activeCat, setActiveCat] = useState("ALL");
    const [livePreview, setLivePreview] = useState(null);

    // Refetch top-15 whenever the active filter chip changes. The closure-
    // captured `requested` prevents a slow request from overwriting a newer
    // one's result.
    useEffect(() => {
        let cancelled = false;
        const requested = activeCat;
        getMenu("queens-long-island", {
            limit: 15,
            sort_key: "POPULAR",
            ...(activeCat !== "ALL" ? { category: activeCat } : {}),
        })
            .then((d) => {
                if (!cancelled) setLivePreview({ ...d, _cat: requested });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [activeCat]);

    // Loading is true whenever the currently-displayed cat doesn't match the
    // user-selected chip yet — derived state, no sync setState in effect.
    const liveLoading = !livePreview || livePreview._cat !== activeCat;

    return (
        <section
            data-testid="live-menu-section"
            className="relative py-12 sm:py-16 px-6 sm:px-10 bg-zen-bg border-t border-zen-ink/8"
        >
            <div className="max-w-[1400px] mx-auto">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-6 gap-6">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-zen-olive mb-4 inline-flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-zen-olive rounded-full animate-pulse" />
                            Live · Updated in real time
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zen-ink leading-[1.05] max-w-2xl">
                            Tonight&rsquo;s top 15 at{" "}
                            <span className="italic text-zen-olive">
                                ZenZest Queens.
                            </span>
                        </h2>
                        {livePreview?.productsCount ? (
                            <p className="mt-4 text-zen-ink-soft">
                                {livePreview.productsCount.toLocaleString()} products
                                in stock. Direct from the point of sale.
                            </p>
                        ) : null}
                    </div>
                    <Link
                        to="/menu/queens-long-island"
                        data-testid="home-live-menu-link"
                        className="zen-link text-[12px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-2"
                    >
                        Browse full menu <ArrowUpRight size={14} />
                    </Link>
                </div>

                {/* Category filter chips */}
                <div
                    data-testid="popular-cat-filters"
                    className="flex flex-wrap gap-2 mb-8 border-b border-zen-ink/8 pb-6 -mx-1 px-1 overflow-x-auto"
                >
                    {[{ slug: "ALL", name: "All" }, ...categories].map((c) => {
                        const active = activeCat === c.slug;
                        return (
                            <button
                                key={c.slug}
                                type="button"
                                onClick={() => setActiveCat(c.slug)}
                                data-testid={`popular-cat-${c.slug.toLowerCase()}`}
                                aria-pressed={active}
                                className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-medium border transition-colors whitespace-nowrap ${
                                    active
                                        ? "bg-zen-ink text-zen-bg border-zen-ink"
                                        : "bg-zen-bg text-zen-ink-soft border-zen-ink/15 hover:border-zen-olive hover:text-zen-ink"
                                }`}
                            >
                                {c.name}
                            </button>
                        );
                    })}
                </div>

                {liveLoading && !livePreview?.products?.length ? (
                    <div
                        data-testid="popular-loading"
                        className="text-sm text-zen-ink-soft py-12"
                    >
                        Loading top sellers&hellip;
                    </div>
                ) : livePreview?.products?.length ? (
                    <div
                        data-testid="popular-products-grid"
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
                    >
                        {livePreview.products.slice(0, 15).map((p) => (
                            <ProductCard
                                key={p.id}
                                product={p}
                                storeSlug="queens-long-island"
                                dutchieStoreSlug={livePreview.dutchie_store_slug}
                            />
                        ))}
                    </div>
                ) : (
                    <p
                        data-testid="popular-empty"
                        className="text-sm text-zen-ink-soft py-12"
                    >
                        No products in this category right now.
                    </p>
                )}
            </div>
        </section>
    );
}
